/* MÂZIA product page (spec 05). Loaded as a module by sections/mz-product-main.liquid, after
   assets/mz-theme.js (window.MZ). Talks to Horizon only through its DOM contracts:
   - option changes: Horizon's <variant-picker> fetches the page for the new variant and fires
     shopify:product:select; every [data-mz-refresh="key"] element is then replaced by the same key from that
     HTML (gallery, tiers, notify link, journey photo, dialogs' product rows, story lines, state JSON);
   - quantity: Bundle & save writes Horizon's quantity input (product-form.js and Razorpay read it);
   - Buy now: Razorpay's own <magic-checkout-btn> (taps reach it under the drawn label);
   - the address bar's ?variant= (Razorpay and Xboost read it): the variant each option change lands on.
   Also: missing-combination fallback, gallery counter / progress pill / lightbox, pincode check (shared by
   every pincode box, localStorage 'mazia-m-pin', event mz:pincode), cutoff line, How it's made dates and
   steps dialog, video-call dialog, WhatsApp links, sticky side columns. While the Diwali delivery promise runs
   (MZ.diwali) the dates are the promise's: delivery in min-max calendar days, no making or dispatch dates. */

const PIN_KEY = 'mazia-m-pin';
const PIN_RE = /^[1-9]\d{5}$/;

const whenMZ = (fn) => {
  if (window.MZ && window.MZ.ready) fn(window.MZ);
  else (window.mzQueue = window.mzQueue || []).push(fn);
};

const readJSON = (element) => {
  if (!element) return {};
  try {
    return JSON.parse(element.textContent || '{}') || {};
  } catch (error) {
    return {};
  }
};

const escapeHtml = (text) =>
  String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const fill = (template, values) =>
  String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));

whenMZ((MZ) => {
  const page = document.querySelector('[data-mz-pdp]');
  if (!page || page.mzReady) return;
  page.mzReady = true;

  const productId = page.dataset.productId;
  const config = readJSON(page.querySelector('[data-mz-pdp-config]'));
  const timeline = readJSON(document.querySelector('[data-mz-timeline-config]'));
  const delivery = (MZ.settings && MZ.settings.delivery) || {};
  const prepaid = (MZ.settings && MZ.settings.prepaid) || {};

  const state = {
    qty: 1,
    pin: '',
    variant: readJSON(page.querySelector('[data-mz-pdp-state]')),
  };

  const formId = `MzProductForm-${productId}`;
  const productForm = () => document.getElementById(formId);
  const quantityInput = () => document.querySelector('[data-mz-qty] input[name="quantity"]');

  /* ---------- The variant in the address bar (?variant=) ----------
     Razorpay's Buy now (magic-shopify.js) and Xboost Bundle take the variant from ?variant= before the form's
     id, so it must name the variant the page shows. Horizon writes it when an option is tapped, from that
     radio's data-variant-id, drawn for the choices before any change still in flight: a second tap on another
     row before the first change lands leaves another variant there. It is written again from the fetched
     variant when each change lands (after Horizon's own write), Buy now waits for a change in flight
     (wireBuyNow), and Add to bag / Buy now check it once more when pressed. */

  let pickerPending = null; // Horizon's promise for the option change in flight (shopify:product:select)

  const setVariantUrl = (id) => {
    try {
      const here = new URL(window.location.href);
      const want = id ? String(id) : '';
      if ((here.searchParams.get('variant') || '') === want) return;
      if (want) here.searchParams.set('variant', want);
      else here.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', here.toString());
    } catch (error) {
      /* URL API unavailable: leave the address as it is */
    }
  };

  // Xboost writes the address bar's variant into the form when a tier is picked: before Add to bag or Buy now
  // reads them, the address bar and the form's id name the shown variant
  const syncShownVariant = () => {
    if (pickerPending || !config.hasOptions) return;
    const id = Number(state.variant.variantId) || 0;
    const bar = document.querySelector('[data-mz-buy-bar]');
    if (!id || (bar && bar.dataset.state !== 'available')) return;
    setVariantUrl(id);
    const input = productForm()?.querySelector('input[name="id"]');
    if (input && input.value !== String(id)) input.value = String(id);
  };

  /* ---------- Money and totals ---------- */

  const rupees = (cents) => Math.round((Number(cents) || 0) / 100);

  const tierTotal = () => {
    const selected = document.querySelector('[data-mz-bundle] [data-mz-tier][aria-checked="true"]');
    if (selected && Number(selected.dataset.mzTier) === state.qty) return Number(selected.dataset.total) || 0;
    return (Number(state.variant.price) || 0) * state.qty;
  };

  const prepaidTotal = (cents) => {
    const pct = Number(prepaid.percent) || 0;
    const total = rupees(cents);
    return (total - Math.round((total * pct) / 100)) * 100;
  };

  const productWords = () => (state.variant.label ? `${config.name} (${state.variant.label})` : config.name || '');

  /* ---------- Bundle & save: quantity, button price, pay-online lines, WhatsApp link ---------- */

  // Xboost Bundle (app embed) replaces Horizon's quantity selector with its own Buy 1 / 2 / 3 tiers (hidden by
  // mz-product.css) and writes the chosen tier's quantity into the product form a moment after it changes;
  // choose its matching tier (radio value = tier index: Buy 1 = 0) so it sends the quantity picked here.
  // It writes into every cart form on the page unless its form selector says otherwise: keep it on this form
  // (also set inline by sections/mz-product-main.liquid before Xboost starts).
  const XB_FORM = '.mz-pdp form[data-type="add-to-cart-form"]';
  const scopeXboost = () => {
    const settings = window.xb_general_settings;
    if (settings && settings.additional_add_to_cart_form_selector !== XB_FORM) settings.additional_add_to_cart_form_selector = XB_FORM;
  };

  const syncXboost = () => {
    scopeXboost();
    const radios = [...document.querySelectorAll('.mz-pdp #xb-quantity-breaks-root input[type="radio"]')];
    const target = radios.find((radio) => Number(radio.value) === state.qty - 1);
    if (target && !target.checked) target.click();
  };

  const syncQuantity = () => {
    const input = quantityInput();
    if (input && input.value !== String(state.qty)) input.value = String(state.qty);
    syncXboost();
  };

  const applyQty = () => {
    const tiers = document.querySelectorAll('[data-mz-bundle] [data-mz-tier]');
    const available = [...tiers].some((tier) => Number(tier.dataset.mzTier) === state.qty);
    if (tiers.length && !available) state.qty = 1;
    tiers.forEach((tier) => {
      const on = Number(tier.dataset.mzTier) === state.qty;
      tier.setAttribute('aria-checked', String(on));
      tier.classList.toggle('is-selected', on);
      tier.tabIndex = on ? 0 : -1;
    });
    syncQuantity();

    const total = tierTotal();
    document.querySelectorAll('[data-mz-add-price]').forEach((el) => {
      el.textContent = MZ.money(total);
    });
    const add = document.querySelector('.mz-pdp-buy__add');
    const addLabel = add && add.querySelector('[data-mz-add-label]');
    if (add && addLabel) {
      const base = add.dataset.label || addLabel.textContent;
      const qtyLabel = add.dataset.labelQty;
      // "Add 2 to bag" on the phone bar only; desktop keeps "Add to bag" with the tier total on the right
      const withQty = add.dataset.state === 'available' && state.qty > 1 && qtyLabel && !MZ.isDesktop();
      addLabel.textContent = withQty ? fill(qtyLabel, { qty: state.qty }) : base;
      add.classList.toggle('has-qty', state.qty > 1);
    }

    const online = prepaidTotal(total);
    const pct = Number(prepaid.percent) || 0;
    document.querySelectorAll('[data-mz-prepaid-line]').forEach((line) => {
      const template = state.qty > 1 ? line.dataset.templateQty : line.dataset.template;
      line.textContent = fill(template, { price: MZ.money(online), qty: state.qty, percent: pct });
    });
    document.querySelectorAll('[data-mz-prepaid-row]').forEach((row) => {
      const template = state.qty > 1 ? row.dataset.templateQty : row.dataset.template;
      const text = row.querySelector('[data-mz-prepaid-text]');
      const price = row.querySelector('[data-mz-prepaid-price]');
      if (text) text.textContent = fill(template, { qty: state.qty, percent: pct });
      if (price) price.textContent = MZ.money(online);
    });

    updateOrderLink();
  };

  const updateOrderLink = () => {
    document.querySelectorAll('[data-mz-contact]').forEach((contact) => {
      const link = contact.querySelector('[data-mz-order-link]');
      if (!link) return;
      let message = fill(contact.dataset.orderMessage, {
        product: productWords(),
        qty: state.qty,
        total: MZ.money(tierTotal()),
      });
      if (state.pin && contact.dataset.orderPin) message += fill(contact.dataset.orderPin, { pincode: state.pin });
      link.href = MZ.whatsappUrl(message);
    });
  };

  const wireTiers = () => {
    document.querySelectorAll('[data-mz-bundle]').forEach((bundle) => {
      if (bundle.mzWired) return;
      bundle.mzWired = true;
      bundle.addEventListener('click', (event) => {
        const tier = event.target instanceof Element ? event.target.closest('[data-mz-tier]') : null;
        if (!tier || tier.getAttribute('aria-disabled') === 'true') return;
        state.qty = Number(tier.dataset.mzTier) || 1;
        applyQty();
      });
      bundle.addEventListener('keydown', (event) => {
        const tier = event.target instanceof Element ? event.target.closest('[data-mz-tier]') : null;
        if (!tier) return;
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const tiers = [...bundle.querySelectorAll('[data-mz-tier]')];
        const next = tiers[(tiers.indexOf(tier) + keys[event.key] + tiers.length) % tiers.length];
        if (!next || next.getAttribute('aria-disabled') === 'true') return;
        state.qty = Number(next.dataset.mzTier) || 1;
        applyQty();
        next.focus();
      });
    });
  };

  /* The quantity and the variant must be right at the moment Horizon or Razorpay reads them */
  ['pointerdown', 'click', 'submit'].forEach((type) =>
    window.addEventListener(
      type,
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest(`#${formId}`) || target.closest('[data-mz-buy-now]') || (target.closest('[form]') && target.closest('[form]').getAttribute('form') === formId)) {
          syncQuantity();
          syncShownVariant();
        }
      },
      true
    )
  );

  /* ---------- Availability (sold out / no such combination) after an option change ---------- */

  const applyAvailability = (resource) => {
    const stateName = resource == null ? 'none' : resource.available === false ? 'sold-out' : 'available';
    document.querySelectorAll('[data-mz-buy-bar]').forEach((bar) => {
      bar.dataset.state = stateName;
    });
    const add = document.querySelector('.mz-pdp-buy__add');
    if (add) add.dataset.state = stateName;
    const fallback = document.querySelector('[data-mz-buy-fallback]');
    if (fallback) fallback.disabled = stateName !== 'available';
    document.querySelectorAll('[data-mz-tier]').forEach((tier) => {
      if (stateName === 'available') tier.removeAttribute('aria-disabled');
      else tier.setAttribute('aria-disabled', 'true');
    });
  };

  /* ---------- Desktop "Added" for 1.8s after a successful add from this page ---------- */

  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise
      ?.then(({ detail } = {}) => {
        if (!detail || detail.didError || detail.source !== 'product-form-component') return;
        if (String(detail.productId) !== String(productId)) return;
        const add = document.querySelector('.mz-pdp-buy__add');
        if (!add) return;
        add.dataset.mzAdded = 'true';
        clearTimeout(add.mzAddedTimer);
        add.mzAddedTimer = setTimeout(() => delete add.dataset.mzAdded, 1800);
      })
      .catch(() => {});
  });

  /* ---------- One add at a time. Shopify takes about a second to answer and the bag opens after that, so
     from the press until the add settles the add buttons (the phone bar's, the desktop one, the How it's made
     band's) are aria-busy and dimmed (mz-product.css), and another press (tap, click, Enter) is dropped
     before Horizon's handlers see it: a quick double tap would otherwise add the piece twice. ---------- */

  const ADD_BUTTONS = '.mz-pdp-buy__add, [data-mz-band-add]';
  const ADD_BUSY_MS = 10000; // no answer at all: presses work again after this
  const ADD_PRESS_MS = 2500; // a press Horizon holds back (an option change in flight) or turns down
  let addsInFlight = 0;
  let addPressed = false;
  let addPressTimer = 0;
  let addBusyTimer = 0;

  const addBusy = () => addPressed || addsInFlight > 0;
  const paintAddBusy = () => {
    const on = addBusy();
    document.querySelectorAll(ADD_BUTTONS).forEach((button) => {
      if (on) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  };
  const dropPress = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  // Window capture runs before Horizon's on:click / on:submit handlers (assets/component.js)
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && target.closest(ADD_BUTTONS) && addBusy()) dropPress(event);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      if (!(event.target instanceof HTMLFormElement) || event.target.id !== formId) return;
      if (addBusy()) {
        dropPress(event);
        return;
      }
      // Busy from the press: Horizon starts the add in this same task, or once an option change has landed
      addPressed = true;
      clearTimeout(addPressTimer);
      addPressTimer = setTimeout(() => {
        addPressed = false;
        paintAddBusy();
      }, ADD_PRESS_MS);
      paintAddBusy();
    },
    true
  );

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const source = event.target instanceof Element ? event.target : null;
    const form = productForm();
    if (!source || !form || !source.contains(form)) return;
    addsInFlight += 1;
    addPressed = false;
    clearTimeout(addPressTimer);
    clearTimeout(addBusyTimer);
    addBusyTimer = setTimeout(() => {
      addsInFlight = 0;
      paintAddBusy();
    }, ADD_BUSY_MS);
    paintAddBusy();
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      addsInFlight = Math.max(0, addsInFlight - 1);
      if (!addsInFlight) clearTimeout(addBusyTimer);
      paintAddBusy();
    };
    Promise.resolve(event.promise).then(done, done);
  });

  /* ---------- Add-to-bag errors: Shopify's sentence ("All 2 Cleo … are in your cart.", "Please try again in
     a few minutes") instead of its heading ("Cart Error", "Too many attempts"). Horizon writes the heading
     right after this event; phones show the message above the fixed bar (mz-product.css).
     An add with no answer Horizon can read (the connection dropped, or an HTML page such as Shopify's rate
     limit) reaches here as SERVICE_UNAVAILABLE without a message, and Horizon only logs it: show the block's
     own sentence (data-mz-error-network) in the same box, for as long as Horizon shows its messages. ---------- */

  const ERROR_SHOW_MS = 10000; // Horizon's ERROR_MESSAGE_DISPLAY_DURATION
  let networkErrorTimer = 0;

  document.addEventListener('shopify:cart:error', (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source || !page.contains(source) || source.closest('product-card, quick-add-dialog, dialog')) return;
    clearTimeout(networkErrorTimer);
    const description = typeof event.detail?.description === 'string' ? event.detail.description.trim() : '';
    if (!description) {
      const box = source.querySelector('[ref="addToCartTextError"]');
      const text = box && event.code === 'SERVICE_UNAVAILABLE' ? (box.dataset.mzErrorNetwork || '').trim() : '';
      if (!text) return;
      const live = source.querySelector('[ref="liveRegion"]');
      box.classList.remove('hidden');
      // The text node after the icon, as Horizon writes it (product-form.js)
      if (box.childNodes[2]) box.childNodes[2].textContent = text;
      else box.appendChild(document.createTextNode(text));
      if (live) live.textContent = text;
      networkErrorTimer = setTimeout(() => {
        box.classList.add('hidden');
        if (live && live.textContent === text) live.textContent = '';
      }, ERROR_SHOW_MS);
      return;
    }
    setTimeout(() => {
      const box = source.querySelector('[ref="addToCartTextError"]');
      const live = source.querySelector('[ref="liveRegion"]');
      if (box && !box.classList.contains('hidden') && box.childNodes[2]) box.childNodes[2].textContent = description;
      if (live && live.textContent) live.textContent = description;
    }, 0);
  });

  /* ---------- Buy now: Razorpay's button under the drawn label ---------- */

  const wireBuyNow = () => {
    const cell = document.querySelector('[data-mz-buy-now]');
    if (!cell || cell.mzWired) return;
    cell.mzWired = true;
    const face = cell.querySelector('.mz-pdp-buy__face');
    const faceLabel = face && face.querySelector('[data-mz-face-label]');
    const idle = faceLabel ? faceLabel.textContent : '';

    const host = () => cell.querySelector('magic-checkout-btn');
    const hostReady = () => {
      const button = host();
      return button && button.style.display !== 'none' ? button : null;
    };
    const mark = () => cell.classList.toggle('is-magic', Boolean(cell.querySelector('.magic-buy-btn__parent')));
    new MutationObserver(mark).observe(cell, { childList: true, subtree: true });
    mark();

    const rest = () => {
      clearTimeout(cell.mzBusyTimer);
      cell.removeAttribute('aria-busy');
      if (faceLabel) faceLabel.textContent = idle;
    };

    const busy = () => {
      if (!face) return;
      cell.setAttribute('aria-busy', 'true');
      if (faceLabel && face.dataset.busy) faceLabel.textContent = face.dataset.busy;
      clearTimeout(cell.mzBusyTimer);
      cell.mzBusyTimer = setTimeout(rest, 2200);
    };

    // A press while an option change is in flight would let Razorpay read the variant Horizon wrote for the
    // tapped value (drawn for the old choices). Hold it, busy, until the change has landed and the address bar
    // names the shown variant (at most 5 s), then pass it on; dropped if that variant can't be bought.
    // Window capture: before Razorpay's own handler on its button.
    let holding = false;
    let passing = false;
    window.addEventListener(
      'click',
      (event) => {
        if (passing || !pickerPending) return;
        const target = event.target instanceof Element ? event.target : null;
        if (!target || !cell.contains(target)) return;
        dropPress(event);
        busy();
        if (holding) return;
        holding = true;
        const started = Date.now();
        const release = () => {
          if (pickerPending && Date.now() - started < 5000) {
            busy();
            setTimeout(release, 150);
            return;
          }
          holding = false;
          const bar = cell.closest('[data-mz-buy-bar]');
          const next = hostReady() || cell.querySelector('[data-mz-buy-fallback]:not([disabled])');
          if ((bar && bar.dataset.state !== 'available') || !next) {
            rest();
            return;
          }
          passing = true;
          try {
            next.click();
          } finally {
            passing = false;
          }
        };
        setTimeout(release, 150);
      },
      true
    );

    const addToBag = () => {
      const form = productForm();
      const submit = form && form.querySelector('button[name="add"]');
      if (form && submit && !submit.disabled) form.requestSubmit(submit);
    };

    cell.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      syncQuantity();
      if (target && target.closest('magic-checkout-btn')) {
        busy();
        return;
      }
      const button = hostReady();
      if (button) {
        event.preventDefault();
        busy();
        button.click();
        return;
      }
      if (!target || !target.closest('[data-mz-buy-fallback]')) return;
      // Razorpay has not drawn its button yet: wait up to 5 s, then add to the bag instead
      busy();
      const started = Date.now();
      const wait = () => {
        const ready = hostReady();
        if (ready) {
          ready.click();
        } else if (Date.now() - started < 5000) {
          setTimeout(wait, 150);
        } else {
          addToBag();
        }
      };
      wait();
    });
  };

  /* ---------- Options: a value whose combination does not exist switches the other options ---------- */

  let lastTapped = ''; // the option value id of the latest change (for retryValue)

  const wirePicker = () => {
    const picker = page.querySelector('variant-picker');
    if (!picker || picker.mzWired) return;
    picker.mzWired = true;
    picker.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        lastTapped = input.dataset.optionValueId || '';
        if (!input.dataset.mzFallback) return;
        for (const id of input.dataset.mzFallback.split(',')) {
          if (!id || id === input.dataset.optionValueId) continue;
          const other = picker.querySelector(`input[data-option-value-id="${CSS.escape(id)}"]`);
          if (other && other !== input) other.checked = true;
        }
      },
      true
    );
  };

  /* ---------- Gallery: mobile counter, desktop progress pill, lightbox ---------- */

  let galleryAbort = null;

  const initGallery = () => {
    galleryAbort?.abort();
    galleryAbort = new AbortController();
    const { signal } = galleryAbort;
    const gallery = page.querySelector('[data-mz-gallery]');
    if (!gallery) return;
    const rail = gallery.querySelector('[data-mz-gallery-rail]');
    const items = [...gallery.querySelectorAll('[data-mz-gallery-item]')];
    const counter = gallery.querySelector('[data-mz-gallery-count]');
    const pill = gallery.querySelector('[data-mz-pill]');
    const count = items.length || 1;

    if (rail) rail.scrollLeft = 0;

    // The rail is a tab stop only where it scrolls sideways (phones, 2+ photos); from 750px the photos are stacked
    const railTab = () => {
      if (!rail) return;
      if (MZ.isDesktop() || items.length < 2) rail.removeAttribute('tabindex');
      else rail.setAttribute('tabindex', '0');
    };
    railTab();
    MZ.mq.desktop.addEventListener?.('change', railTab, { signal });

    let frame = 0;
    const onRail = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!rail || !counter || MZ.isDesktop()) return;
        const index = Math.min(count - 1, Math.max(0, Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth))));
        counter.textContent = `${index + 1} / ${count}`;
      });
    };
    rail?.addEventListener('scroll', onRail, { passive: true, signal });

    let pillFrame = 0;
    const onPage = () => {
      if (pillFrame || !pill) return;
      pillFrame = requestAnimationFrame(() => {
        pillFrame = 0;
        if (!MZ.isDesktop()) return;
        const middle = window.innerHeight / 2;
        let best = 0;
        let bestDistance = Infinity;
        items.forEach((item, index) => {
          const box = item.getBoundingClientRect();
          const distance = Math.abs(box.top + box.height / 2 - middle);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = index;
          }
        });
        pill.style.transform = `translateX(${best * 100}%)`;
      });
    };
    if (pill) {
      window.addEventListener('scroll', onPage, { passive: true, signal });
      window.addEventListener('resize', onPage, { passive: true, signal });
      onPage();
    }

    gallery.addEventListener(
      'click',
      (event) => {
        const zoom = event.target instanceof Element ? event.target.closest('[data-mz-zoom]') : null;
        if (!zoom || !MZ.isDesktop()) return;
        openLightbox(Number(zoom.dataset.mzZoom) || 0, zoom);
      },
      { signal }
    );
  };

  const lightbox = document.querySelector('[data-mz-lightbox]');
  let lightboxIndex = 0;
  let lightboxReturn = null;

  const lightboxItems = () =>
    [...page.querySelectorAll('[data-mz-gallery] [data-mz-gallery-item]')].map((item) => ({
      full: item.dataset.full,
      thumb: item.dataset.thumb,
      alt: item.dataset.alt || '',
    }));

  const showLightbox = (index) => {
    if (!lightbox) return;
    const list = lightboxItems();
    if (!list.length) return;
    lightboxIndex = (index + list.length) % list.length;
    const item = list[lightboxIndex];
    const stage = lightbox.querySelector('[data-mz-lightbox-stage]');
    let image = stage.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      image.className = 'mz-lightbox__img';
      image.decoding = 'async';
      image.width = 2000;
      image.height = 2000;
      stage.prepend(image);
    }
    image.src = item.full;
    image.alt = item.alt;
    const pad = (n) => String(n).padStart(2, '0');
    // A live region (blocks/mz-product-gallery.liquid): "02 / 07" is read out on each step
    const count = lightbox.querySelector('[data-mz-lightbox-count]');
    const countText = `${pad(lightboxIndex + 1)} / ${pad(list.length)}`;
    if (count.textContent !== countText) count.textContent = countText;
    const thumbs = lightbox.querySelector('[data-mz-lightbox-thumbs]');
    if (thumbs.childElementCount !== list.length) {
      const label = lightbox.dataset.thumbLabel || '';
      thumbs.innerHTML = list
        .map(
          (entry, i) =>
            `<button type="button" class="mz-lightbox__thumb" data-mz-lightbox-go="${i}" aria-label="${escapeHtml(fill(label, { n: i + 1 }))}"><img src="${escapeHtml(entry.thumb)}" alt="" width="56" height="56" loading="lazy"></button>`
        )
        .join('');
    }
    thumbs.querySelectorAll('[data-mz-lightbox-go]').forEach((thumb, i) => {
      thumb.classList.toggle('is-current', i === lightboxIndex);
      thumb.setAttribute('aria-current', i === lightboxIndex ? 'true' : 'false');
    });
    lightbox.classList.toggle('is-single', list.length < 2);
    keepThumbInView();
  };

  // A thumbnail row wider than the screen scrolls (mz-product.css): keep the current one in view
  const keepThumbInView = () => {
    const thumbs = lightbox && lightbox.querySelector('[data-mz-lightbox-thumbs]');
    const current = thumbs && thumbs.children[lightboxIndex];
    if (!current || thumbs.scrollWidth <= thumbs.clientWidth) return;
    const row = thumbs.getBoundingClientRect();
    const box = current.getBoundingClientRect();
    const pad = 32;
    if (box.left < row.left + pad) thumbs.scrollLeft -= row.left + pad - box.left;
    else if (box.right > row.right - pad) thumbs.scrollLeft += box.right - (row.right - pad);
  };

  const openLightbox = (index, from) => {
    if (!lightbox) return;
    lightboxReturn = from || null;
    const thumbs = lightbox.querySelector('[data-mz-lightbox-thumbs]');
    thumbs.innerHTML = '';
    thumbs.scrollLeft = 0;
    showLightbox(index);
    if (!lightbox.open) lightbox.showModal();
    keepThumbInView();
    MZ.lockScroll('mz-lightbox');
  };

  if (lightbox) {
    lightbox.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-mz-lightbox-close]')) lightbox.close();
      const step = target.closest('[data-mz-lightbox-step]');
      if (step) showLightbox(lightboxIndex + Number(step.dataset.mzLightboxStep));
      const go = target.closest('[data-mz-lightbox-go]');
      if (go) showLightbox(Number(go.dataset.mzLightboxGo));
    });
    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') showLightbox(lightboxIndex + 1);
      if (event.key === 'ArrowLeft') showLightbox(lightboxIndex - 1);
    });
    lightbox.addEventListener('close', () => {
      MZ.unlockScroll('mz-lightbox');
      lightboxReturn?.focus?.({ preventScroll: true });
    });
  }

  /* ---------- Delivery dates (MZ.delivery: the merchant's working days, in India time) ---------- */

  const bands = (() => {
    const lines = String(timeline.bands || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    return lines
      .map((line) => {
        const [left, right] = line.split(':');
        if (!left || !right) return null;
        const [min, max] = right.split('-').map((n) => parseInt(n, 10));
        if (Number.isNaN(min)) return null;
        const prefixes = left.split(',').flatMap((part) => {
          const range = part.trim().match(/^(\d+)\s*-\s*(\d+)$/);
          if (range) {
            const out = [];
            for (let n = Number(range[1]); n <= Number(range[2]) && out.length < 100; n += 1) out.push(String(n).padStart(range[1].length, '0'));
            return out;
          }
          return part.trim() ? [part.trim()] : [];
        });
        return { prefixes, min, max: Number.isNaN(max) ? min : max };
      })
      .filter(Boolean);
  })();

  const overridesFor = (pin) => {
    if (!pin) return {};
    for (const band of bands) {
      if (band.prefixes.some((prefix) => pin.startsWith(prefix))) return { transitMin: band.min, transitMax: band.max };
    }
    return {};
  };

  /* While the Diwali delivery promise runs (MZ.diwali: Theme settings → MÂZIA → Diwali delivery promise) every
     date on the page is the promise's: delivery today + min to today + max calendar days, guaranteed. The
     made-to-order days it does not give stay off the page (no making or dispatch dates). */
  const diwaliAt = (now) => (typeof MZ.diwali === 'function' ? MZ.diwali(now) : { active: false, copy: {} });

  const plan = (now = new Date()) => {
    const d = MZ.delivery(now, overridesFor(state.pin));
    const counted = d.cutoffPassed ? MZ.addDays(d.today, 1) : d.today;
    const w = (n) => d.addWorkingDays(counted, n);
    const startNow = w(1);
    const startLater = d.addWorkingDays(MZ.addDays(d.today, 1), 1);
    return { ...d, w, counted, startNow, startLater, diwali: diwaliAt(now) };
  };

  // The dates on the page move when this changes: the start day, the Diwali promise starting or ending, its
  // order-by day passing, midnight in India
  const datesKey = (p) => [p.startNow.getTime(), p.diwali.active, p.diwali.beforeCutoff, p.diwali.today?.getTime?.() ?? 0].join('|');

  const cutoffText = (p) => {
    if (p.diwali.active) return MZ.diwaliText(p.diwali.copy.line, p.diwali);
    const copy = timeline.cutoff || {};
    const tomorrow = MZ.addDays(p.today, 1).getTime() === p.startNow.getTime();
    const day = tomorrow ? copy.tomorrow : fill(copy.on, { date: MZ.formatDate(p.startNow, { weekday: true }) });
    const countdown = delivery.countdown !== false && !p.cutoffPassed && p.startNow.getTime() < p.startLater.getTime() && p.msToCutoff > 0;
    if (countdown) return fill(copy.countdown, { time: MZ.formatCountdown(p.msToCutoff), day });
    return fill(copy.today, { day });
  };

  const daysNote = () => {
    const names = timeline.days || {};
    return names[delivery.days] || names.mon_fri || '';
  };

  const renderDates = () => {
    const p = plan();
    const cutoff = cutoffText(p);
    document.querySelectorAll('[data-mz-cutoff]').forEach((el) => {
      el.textContent = cutoff;
    });
    const dw = p.diwali.active ? p.diwali : null;
    document.querySelectorAll('[data-mz-stop-date]').forEach((el) => {
      const template = el.dataset.template || '';
      switch (el.dataset.mzStopDate) {
        case 'make':
          el.textContent = dw
            ? MZ.diwaliText(dw.copy.stopMake, dw)
            : fill(template, { dates: MZ.formatRange(p.startNow, p.dispatchFrom, { spaced: false }) });
          break;
        case 'ship':
          el.textContent = dw ? MZ.diwaliText(dw.copy.stopShip, dw) : fill(template, { date: MZ.formatDate(p.dispatchTo) });
          break;
        case 'door':
          el.textContent = dw ? MZ.diwaliText(dw.copy.stopDoor, dw) : fill(template, { date: MZ.formatDate(p.deliverTo) });
          break;
        default:
          el.textContent = template;
      }
    });
    document.querySelectorAll('[data-mz-days-note]').forEach((el) => {
      if (!el.dataset.template) el.dataset.template = el.textContent;
      el.textContent = fill(el.dataset.template, { days: daysNote() });
    });
    renderPincodes(p);
    renderSteps(p);
    return p;
  };

  /* ---------- Steps dialog ---------- */

  const renderSteps = (p) => {
    const holder = document.querySelector('[data-mz-steps]');
    const s = timeline.steps;
    if (!holder || !s) return;
    const processMin = Number(delivery.processMin) || 7;
    const processMax = Number(delivery.processMax) || processMin;
    const span = (a, b) => MZ.formatRange(a, b, { weekday: true });
    const at = (n) => p.w(Math.max(1, Math.min(processMin, n)));
    const shapeTo = Math.max(2, Math.round((processMin * 4) / 7));
    const finishFrom = Math.min(processMin, shapeTo + 1);
    const finishTo = Math.max(finishFrom, processMin - 1);
    const names = (s.byMaterial && (s.byMaterial[config.group] || s.byMaterial.hand)) || [];
    const shape = state.variant.shape || '';
    const shadeWords = shape ? `${shape} ${String(s.shadeWord || 'shade').toLowerCase()}`.trim() : '';
    let fifth;
    if (config.lamp && shape) fifth = { name: fill(s.wireShadeName, { shape, shade: shadeWords }), sub: fill(s.wireShadeSub, { shape, shade: shadeWords }) };
    else if (config.lamp) fifth = { name: s.wireName, sub: s.wireSub };
    else fifth = { name: s.qcName, sub: s.qcSub };

    const transitMax = Number(overridesFor(state.pin).transitMax ?? delivery.transitMax) || 1;
    const roadFrom = p.addWorkingDays(p.dispatchFrom, 1);
    let roadTo = transitMax > 1 ? p.addWorkingDays(p.dispatchTo, transitMax - 1) : roadFrom;
    if (roadTo.getTime() < roadFrom.getTime()) roadTo = roadFrom;

    // Diwali delivery promise: the same steps, dated only where the promise gives a date (today, at your door)
    const dw = p.diwali.active ? p.diwali : null;
    const groups = dw
      ? [
          {
            icon: 'make',
            title: s.makeTitle,
            sub: s.makeSub,
            rows: [
              { name: s.orderName, when: fill(s.today, { date: MZ.formatDate(p.today, { weekday: true }) }), sub: s.orderSub },
              { name: names[0] },
              { name: names[1] },
              { name: names[2] },
              { name: fifth.name, sub: fifth.sub },
            ],
          },
          {
            icon: 'road',
            title: s.roadTitle,
            sub: state.pin ? fill(s.roadSubPin, { pincode: state.pin }) : s.roadSub,
            rows: [
              { name: s.shipName },
              { name: s.roadName, sub: s.roadStepSub },
              { name: s.doorName, when: span(dw.from, dw.to), sub: MZ.diwaliText(dw.copy.stepDoor, dw) },
            ],
          },
        ]
      : [
          {
            icon: 'make',
            title: s.makeTitle,
            sub: s.makeSub,
            rows: [
              { name: s.orderName, when: fill(s.today, { date: MZ.formatDate(p.today, { weekday: true }) }), sub: s.orderSub },
              { name: names[0], when: span(at(1), at(1)) },
              { name: names[1], when: span(at(2), at(shapeTo)) },
              { name: names[2], when: span(at(finishFrom), at(finishTo)) },
              { name: fifth.name, when: span(at(processMin), at(processMin)), sub: fifth.sub },
            ],
          },
          {
            icon: 'road',
            title: s.roadTitle,
            sub: state.pin ? fill(s.roadSubPin, { pincode: state.pin }) : s.roadSub,
            rows: [
              { name: s.shipName, when: span(p.dispatchFrom, p.dispatchTo), sub: fill(s.shipSub, { min: processMin, max: processMax }) },
              { name: s.roadName, when: span(roadFrom, roadTo), sub: s.roadStepSub },
              { name: s.doorName, when: span(p.deliverFrom, p.deliverTo), sub: state.pin ? fill(s.doorSubPin, { pincode: state.pin }) : s.doorSub },
            ],
          },
        ];
    const icon = (kind) => document.querySelector(`[data-mz-step-icon="${kind}"]`)?.innerHTML || '';
    holder.innerHTML = groups
      .map(
        (group) => `
        <div class="mz-pdp-steps__group mz-pdp-steps__group--${group.icon}">
          <div class="mz-pdp-steps__head">
            <span class="mz-pdp-steps__disc" aria-hidden="true">${icon(group.icon)}</span>
            <span class="mz-pdp-steps__heading">
              <span class="mz-pdp-steps__title">${escapeHtml(group.title)}</span>
              <span class="mz-pdp-steps__sub">${escapeHtml(group.sub)}</span>
            </span>
          </div>
          <ol class="mz-pdp-steps__rows">
            ${group.rows
              .filter((row) => row.name)
              .map(
                (row) => `
              <li class="mz-pdp-steps__row">
                <span class="mz-pdp-steps__mark" aria-hidden="true"></span>
                <span class="mz-pdp-steps__text">
                  <span class="mz-pdp-steps__name">${escapeHtml(row.name)}</span>
                  ${row.when ? `<span class="mz-pdp-steps__when mz-tnum">${escapeHtml(row.when)}</span>` : ''}
                  ${row.sub ? `<span class="mz-pdp-steps__line">${escapeHtml(row.sub)}</span>` : ''}
                </span>
              </li>`
              )
              .join('')}
          </ol>
        </div>`
      )
      .join('');
  };

  /* ---------- Pincode boxes ---------- */

  const storedPin = () => {
    try {
      const value = window.localStorage.getItem(PIN_KEY) || '';
      return PIN_RE.test(value) ? value : '';
    } catch (error) {
      return '';
    }
  };

  const storePin = (value) => {
    try {
      window.localStorage.setItem(PIN_KEY, value);
    } catch (error) {
      /* private mode: remembered for this page only */
    }
  };

  const errors = new WeakSet();

  // The boxes' status lines are live regions: write them only when their words change, so a re-render
  // (dates every 30 s, an option change, the other box's check) is not announced again
  const setText = (el, text) => {
    if (el.mzHtml !== undefined) {
      el.mzHtml = undefined;
      el.textContent = text;
    } else if (el.textContent !== text) el.textContent = text;
  };
  // The same for words with markup (the dates in a span); `text` is their plain words, which Liquid may have
  // printed already with the same markup (the Diwali status line)
  const setHtml = (el, html, text) => {
    if (el.mzHtml === html) return;
    const printed = el.mzHtml === undefined && text !== undefined && el.textContent === text && el.querySelector('.mz-diwali__hl');
    el.mzHtml = html;
    if (!printed) el.innerHTML = html;
  };

  const renderPincodes = (p) => {
    const d = p || plan();
    const dw = d.diwali && d.diwali.active ? d.diwali : null;
    const dates = dw ? MZ.formatRange(dw.from, dw.to) : MZ.formatRange(d.deliverFrom, d.deliverTo);
    // While the Diwali promise runs a check keeps its words (Theme settings → MÂZIA): "Guaranteed delivery to
    // 342012 by Fri 30 Oct" on phones, "Free · guaranteed by Fri 30 Oct to 342012" on desktop, the date in a
    // nowrap span (.mz-diwali__hl); hl false gives the plain words
    const diwaliWords = (template, hl) => fill(MZ.diwaliText(template, dw, { hl }), { pincode: state.pin });
    document.querySelectorAll('[data-mz-pincode]').forEach((box) => {
      const input = box.querySelector('[data-mz-pin-input]');
      if (input && document.activeElement !== input && state.pin && !input.value) input.value = state.pin;
      const failed = errors.has(box);
      if (input) {
        if (failed) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
      const error = box.querySelector('[data-mz-pin-error]');
      if (error) {
        setText(error, failed ? box.dataset.error || '' : '');
        error.hidden = !failed;
      }
      const fallback = box.querySelector('[data-mz-pin-default]');
      const result = box.querySelector('[data-mz-pin-result]');
      const line = box.querySelector('[data-mz-pin-result-line]');
      // Before a check: "Estimated delivery by Mon 16 Nov"; none while the Diwali promise runs (its box says it)
      if (fallback) {
        fallback.hidden = failed || Boolean(state.pin) || Boolean(dw);
        if (fallback.dataset.template) setText(fallback, fill(fallback.dataset.template, { date: MZ.formatDate(d.deliverTo, { weekday: true }) }));
      }
      box.querySelectorAll('[data-mz-pin-note]').forEach((note) => {
        note.hidden = Boolean(dw);
      });
      if (result) result.hidden = failed || !state.pin;
      if (line && state.pin) {
        if (dw && dw.copy.pinResult) {
          setHtml(line, diwaliWords(dw.copy.pinResult, true));
        } else {
          const parts = String(box.dataset.result || '').split('[dates]');
          setHtml(
            line,
            `${escapeHtml(fill(parts[0], { pincode: state.pin }))}<strong class="mz-pdp-delivery__dates">${escapeHtml(dates)}</strong>${escapeHtml(fill(parts.slice(1).join(''), { pincode: state.pin }))}`
          );
        }
      }
      const status = box.querySelector('[data-mz-pin-status]');
      if (status) {
        status.classList.toggle('is-error', failed);
        const diwaliStatus = dw && (state.pin ? dw.copy.pinResultDesktop : dw.copy.pinStatus);
        if (failed) setText(status, box.dataset.error || '');
        else if (diwaliStatus) setHtml(status, diwaliWords(diwaliStatus, true), diwaliWords(diwaliStatus, false));
        else if (state.pin) setText(status, fill(box.dataset.resultDesktop, { dates, pincode: state.pin }));
        else setText(status, box.dataset.defaultDesktop || status.textContent);
      }
      // The band's label: "Add your pincode for exact dates"; while the Diwali promise runs every order has the
      // same guaranteed dates, so its own words ("Check delivery to your pincode", after a check "Guaranteed for
      // pincode 342012")
      const label = box.querySelector('[data-mz-pin-label]');
      if (label) {
        if (label.dataset.template === undefined) label.dataset.template = label.textContent;
        const diwaliLabel = dw && (state.pin ? dw.copy.bandLabelPin : dw.copy.bandLabel);
        if (diwaliLabel) setText(label, diwaliWords(diwaliLabel, false));
        else setText(label, state.pin ? fill(label.dataset.templatePin, { pincode: state.pin }) : label.dataset.template);
      }
    });
  };

  const wirePincodes = () => {
    document.querySelectorAll('[data-mz-pincode]').forEach((box) => {
      if (box.mzWired) return;
      box.mzWired = true;
      const form = box.querySelector('[data-mz-pin-form]');
      const input = box.querySelector('[data-mz-pin-input]');
      if (!form || !input) return;
      const placeholderMobile = input.placeholder;
      const placeholderDesktop = input.dataset.placeholderDesktop;
      const setPlaceholder = () => {
        if (placeholderDesktop) input.placeholder = MZ.isDesktop() ? placeholderDesktop : placeholderMobile;
      };
      setPlaceholder();
      MZ.mq.desktop.addEventListener?.('change', setPlaceholder);
      input.addEventListener('input', () => {
        const clean = input.value.replace(/\D/g, '').slice(0, 6);
        if (clean !== input.value) input.value = clean;
        if (errors.has(box)) {
          errors.delete(box);
          renderPincodes();
        }
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const value = input.value.replace(/\D/g, '').slice(0, 6);
        if (!PIN_RE.test(value)) {
          // Only this box says so (its live region and aria-invalid); the dates, the other box and the
          // WhatsApp message keep the last pincode that checked out
          errors.add(box);
          renderPincodes();
          return;
        }
        errors.delete(box);
        state.pin = value;
        storePin(value);
        document.querySelectorAll('[data-mz-pin-input]').forEach((other) => {
          other.value = value;
        });
        if (!MZ.isDesktop()) input.blur();
        MZ.emit('pincode', { pin: value });
        renderDates();
        updateOrderLink();
      });
    });
  };

  /* ---------- Dialogs: steps and video call (native <dialog>; sheet on phones) ---------- */

  const openDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    MZ.lockScroll('mz-pdp-dialog');
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  };

  document.querySelectorAll('[data-mz-dialog]').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (target === dialog || (target instanceof Element && target.closest('[data-mz-dialog-close]'))) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-open');
      MZ.unlockScroll('mz-pdp-dialog');
    });
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-mz-open-steps]')) {
      renderDates();
      openDialog(document.querySelector('[data-mz-dialog="steps"]'));
    }
    if (target.closest('[data-mz-open-video]')) openVideo();
    const jump = target.closest('[data-mz-scroll-journey]');
    if (jump) {
      const band = document.getElementById('MzJourney');
      if (band && band.offsetParent !== null) {
        event.preventDefault();
        const top = band.getBoundingClientRect().top + window.scrollY - 48;
        window.scrollTo({ top: Math.max(0, top), behavior: MZ.reducedMotion ? 'auto' : 'smooth' });
        // Focus goes with the scroll, as a plain #MzJourney jump would take it: the next Tab continues
        // from the band instead of the link, now off screen
        const heading = band.querySelector('.mz-pdp-band__title') || band;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }
  });

  /* Video call */
  const video = { slot: '', location: '' };

  const videoMessage = (contact) => {
    let message = fill(contact.dataset.videoMessage, { product: productWords(), time: video.slot });
    if (video.location && contact.dataset.videoLocation) message += fill(contact.dataset.videoLocation, { location: video.location });
    return message;
  };

  const renderVideo = () => {
    const dialog = document.querySelector('[data-mz-dialog="video"]');
    const contact = document.querySelector('[data-mz-contact]');
    if (!dialog || !contact) return;
    dialog.querySelectorAll('[data-mz-slot]').forEach((slot) => {
      const on = slot.dataset.mzSlot === video.slot;
      slot.setAttribute('aria-checked', String(on));
      slot.tabIndex = on || (!video.slot && slot === dialog.querySelector('[data-mz-slot]')) ? 0 : -1;
    });
    const send = dialog.querySelector('[data-mz-video-send]');
    const wait = dialog.querySelector('[data-mz-video-wait]');
    if (send) {
      send.hidden = !video.slot;
      send.href = MZ.whatsappUrl(videoMessage(contact));
    }
    if (wait) wait.hidden = Boolean(video.slot);
  };

  const openVideo = () => {
    const dialog = document.querySelector('[data-mz-dialog="video"]');
    if (!dialog) return;
    const form = dialog.querySelector('[data-mz-video-form]');
    const done = dialog.querySelector('[data-mz-video-done]');
    const location = dialog.querySelector('[data-mz-video-location]');
    if (form) form.hidden = false;
    if (done) done.hidden = true;
    if (location && !location.value && state.pin) location.value = state.pin;
    video.location = location ? location.value.trim() : '';
    renderVideo();
    openDialog(dialog);
  };

  const wireVideo = () => {
    const dialog = document.querySelector('[data-mz-dialog="video"]');
    const contact = document.querySelector('[data-mz-contact]');
    if (!dialog || !contact || dialog.mzWired) return;
    dialog.mzWired = true;
    dialog.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const slot = target && target.closest('[data-mz-slot]');
      if (slot) {
        video.slot = slot.dataset.mzSlot;
        renderVideo();
      }
      if (target && target.closest('[data-mz-video-send]')) {
        const done = dialog.querySelector('[data-mz-video-done]');
        const text = dialog.querySelector('[data-mz-video-done-text]');
        setTimeout(() => {
          if (text) text.textContent = fill(contact.dataset.doneText, { time: video.slot.replace(/\s*[–-]\s*/, ' and ') });
          dialog.querySelector('[data-mz-video-form]').hidden = true;
          if (done) done.hidden = false;
          done?.querySelector('[data-mz-dialog-close]')?.focus();
        }, 300);
      }
    });
    dialog.addEventListener('keydown', (event) => {
      const slot = event.target instanceof Element ? event.target.closest('[data-mz-slot]') : null;
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!slot || !(event.key in keys)) return;
      event.preventDefault();
      const slots = [...dialog.querySelectorAll('[data-mz-slot]')];
      const next = slots[(slots.indexOf(slot) + keys[event.key] + slots.length) % slots.length];
      video.slot = next.dataset.mzSlot;
      renderVideo();
      next.focus();
    });
    const location = dialog.querySelector('[data-mz-video-location]');
    location?.addEventListener('input', () => {
      video.location = location.value.slice(0, 40).trim();
      renderVideo();
    });
  };

  /* ---------- Sticky side columns: stick 120px down, or scroll until their end is on screen ---------- */

  const stickies = [...page.querySelectorAll('[data-mz-sticky]')];
  const topOffset = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mz-top-offset')) || 96;
  const fitSticky = () => {
    const offset = topOffset() + 24;
    stickies.forEach((el) => {
      if (getComputedStyle(el).position !== 'sticky') {
        el.style.removeProperty('top');
        return;
      }
      el.style.top = `${Math.min(offset, window.innerHeight - el.offsetHeight - 24)}px`;
    });
  };
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => fitSticky());
    stickies.forEach((el) => observer.observe(el));
  }
  window.addEventListener('resize', fitSticky, { passive: true });

  /* Keyboard focus inside a stuck column taller than the screen: the browser scrolls the page, but the column
     stays stuck, so the control stays under the header or above the screen. Scroll to the column's own
     (unstuck) place instead, with the control 24px under the header (WCAG 2.4.11). The gallery shares the
     columns' grid row, so its top is where they start. Pointer focus is left alone. */
  let keyboardFocus = false;
  document.addEventListener('keydown', () => {
    keyboardFocus = true;
  }, true);
  ['pointerdown', 'mousedown', 'touchstart'].forEach((type) =>
    document.addEventListener(type, () => {
      keyboardFocus = false;
    }, { capture: true, passive: true })
  );

  const stuckColumn = (element) => {
    let node = element.closest('[data-mz-sticky]');
    while (node) {
      if (getComputedStyle(node).position === 'sticky') return node;
      node = node.parentElement ? node.parentElement.closest('[data-mz-sticky]') : null;
    }
    return null;
  };

  page.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!keyboardFocus || !target || !MZ.isDesktop()) return;
    const column = stuckColumn(target);
    const media = page.querySelector('.mz-pdp__media');
    if (!column || !media) return;
    // A visually hidden radio sits in its label's corner: measure what is drawn
    const shown = target.closest('label') || target;
    const clear = topOffset() + 8;
    if (shown.getBoundingClientRect().top >= clear) return;
    requestAnimationFrame(() => {
      const within = shown.getBoundingClientRect().top - column.getBoundingClientRect().top;
      const start = media.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, Math.round(start + within - (topOffset() + 24))), behavior: 'instant' });
    });
  });

  /* From 1180px the story column is drawn first (left of the gallery): put it first in the DOM as well, so Tab
     and screen readers follow the columns as the design's page does. Below 1180px it ends the side column. */
  const wideLayout = window.matchMedia('(min-width: 1180px)');
  const placeStory = () => {
    const grid = page.querySelector('.mz-pdp__grid');
    const side = page.querySelector('.mz-pdp__side');
    const story = page.querySelector('.mz-pdp__story');
    const media = page.querySelector('.mz-pdp__media');
    if (!grid || !side || !story || !media) return;
    if (wideLayout.matches) {
      if (story.nextElementSibling !== media) grid.insertBefore(story, media);
    } else if (story.parentElement !== side) {
      side.appendChild(story);
    }
  };
  placeStory();
  wideLayout.addEventListener?.('change', () => {
    placeStory();
    fitSticky();
  });

  /* ---------- Option change: refresh the server-rendered parts ---------- */

  const refreshFrom = (html) => {
    document.querySelectorAll('[data-mz-refresh]').forEach((el) => {
      if (el.closest('quick-add-dialog, .quick-add-modal')) return;
      const key = el.getAttribute('data-mz-refresh');
      const fresh = html.querySelector(`[data-mz-refresh="${CSS.escape(key)}"]`);
      if (fresh) el.replaceWith(document.importNode(fresh, true));
    });
  };

  /* Two quick taps on different rows: the second value's data-variant-id and data-mz-fallback were drawn for
     the choices before the first change, so Horizon can ask for a combination that does not exist (Kallio:
     beige, then Drum). The page would then show the cheapest variant's choices with nothing to buy. Tap that
     value once more in the fresh options instead: its fresh fallback (wirePicker) leads to a combination that
     exists, as a single tap does. Never twice in a row. */
  let retryNext = false;
  const retryValue = (optionValueId) => {
    const picker = page.querySelector('variant-picker');
    const input = picker && picker.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (!input) return;
    input.checked = true;
    retryNext = true;
    try {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      retryNext = false;
    }
  };

  document.addEventListener('shopify:product:select', (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source || !page.contains(source) || source.closest('product-card, quick-add-dialog, dialog')) return;
    const promise = event.promise;
    if (!promise) return;
    const retried = retryNext;
    retryNext = false;
    const tapped = String(event.detail?.optionValueId || lastTapped || '');
    pickerPending = promise;
    promise
      .then(({ detail } = {}) => {
        if (!detail || !detail.html) return;
        const resource = detail.resource ?? null;
        const latest = pickerPending === promise;
        // The address bar names the variant this change landed on, not the one Horizon wrote at the tap
        // (its write waits a frame after the tap; a reply quicker than that is written over once more)
        if (latest) {
          setVariantUrl(resource && resource.id);
          requestAnimationFrame(() =>
            setTimeout(() => {
              if (!pickerPending) setVariantUrl(resource && resource.id);
            }, 0)
          );
        }
        refreshFrom(detail.html);
        state.variant = readJSON(page.querySelector('[data-mz-pdp-state]'));
        applyAvailability(resource);
        wireTiers();
        applyQty();
        // Horizon morphs the Add to bag button after this resolves too: apply the tier price once more
        requestAnimationFrame(() => applyQty());
        initGallery();
        renderDates();
        if (latest && resource == null && tapped && !retried) retryValue(tapped);
      })
      .catch(() => {})
      .finally(() => {
        if (pickerPending === promise) pickerPending = null;
      });
  });

  /* ---------- Judge.me reviews: dates as the design writes them ("27 Aug 2026"); long quotes a size down
     (the design's quotes are one line; real reviews run to a paragraph) ---------- */

  const LONG_QUOTE = 140;

  const reviewDates = (scope) => {
    scope.querySelectorAll('.jdgm-rev__timestamp[datetime]').forEach((time) => {
      const date = new Date(time.getAttribute('datetime'));
      if (Number.isNaN(date.getTime())) return;
      const day = MZ.ist(date);
      const text = `${MZ.formatDate(day.date)} ${day.year}`;
      if (time.textContent !== text) time.textContent = text;
    });
    scope.querySelectorAll('.jdgm-rev__body').forEach((body) => {
      const long = (body.textContent || '').trim().length > LONG_QUOTE;
      if (body.classList.contains('mz-quote-long') !== long) body.classList.toggle('mz-quote-long', long);
    });
  };
  // The desktop count follows what Judge.me's widget says once it has drawn (its data-number-of-reviews);
  // a widget with none hides the section (mz-product.css), so the count never reads "1 review" over nothing.
  const reviewCount = (scope) => {
    const widget = scope.querySelector('.jdgm-rev-widg[data-number-of-reviews]');
    const count = scope.querySelector('[data-mz-reviews-count]');
    if (!widget || !count) return;
    const n = parseInt(widget.getAttribute('data-number-of-reviews'), 10);
    if (Number.isNaN(n)) return;
    const text = fill(n === 1 ? scope.dataset.countOne : scope.dataset.countOther, { count: n });
    if (count.textContent !== text) count.textContent = text;
    if (count.hidden !== (n === 0)) count.hidden = n === 0;
  };
  const reviews = document.querySelector('[data-mz-reviews]');
  if (reviews) {
    const syncReviews = () => {
      reviewDates(reviews);
      reviewCount(reviews);
    };
    syncReviews();
    new MutationObserver(syncReviews).observe(reviews, { childList: true, subtree: true, characterData: true });
  }

  /* ---------- Start ---------- */

  // The page may open on the cheapest variant without ?variant= in the URL. Xboost Bundle (app embed)
  // takes the variant for its add-to-cart from that parameter and otherwise uses the product's first variant,
  // so put the shown variant in the URL, as Horizon does after every option change.
  try {
    const here = new URL(window.location.href);
    if (config.hasOptions && state.variant.variantId && !here.searchParams.get('variant')) {
      here.searchParams.set('variant', String(state.variant.variantId));
      window.history.replaceState(window.history.state, '', here.toString());
    }
  } catch (error) {
    /* URL API unavailable: leave the address as it is */
  }

  state.pin = storedPin();
  if (state.pin) {
    document.querySelectorAll('[data-mz-pin-input]').forEach((input) => {
      input.value = state.pin;
    });
  }
  wirePicker();
  wireTiers();
  wireBuyNow();
  wirePincodes();
  wireVideo();
  applyQty();
  MZ.mq.desktop.addEventListener?.('change', () => applyQty());
  initGallery();
  let lastKey = datesKey(renderDates());
  fitSticky();
  window.addEventListener('load', fitSticky, { once: true });

  // Every 30 s: the countdown text, and every date when the start day moves (cutoff or midnight) or the Diwali
  // promise changes state (its order-by day or last day passing)
  setInterval(() => {
    const p = plan();
    const key = datesKey(p);
    if (key !== lastKey) {
      lastKey = key;
      renderDates();
    } else {
      const text = cutoffText(p);
      document.querySelectorAll('[data-mz-cutoff]').forEach((el) => {
        el.textContent = text;
      });
    }
  }, 30000);
});
